import { useQuery } from "@tanstack/react-query";
import { formatCurrency, formatNumber } from "../lib/utils";
import {
  Users,
  UserPlus,
  CreditCard,
  TrendingUp,
  Bot,
  Activity,
} from "lucide-react";

interface Metrics {
  mau: number;
  dauToday: number;
  totalUsers: number;
  payingUsers: number;
  totalRevenue: number;
  monthlyRevenue: number;
  totalBots: number;
  newUsersWeek: number;
}

function MetricCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  description?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">
          {title}
        </span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { data: metrics, isLoading } = useQuery<Metrics>({
    queryKey: ["/api/admin/metrics"],
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Дашборд</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-card p-6 animate-pulse"
            >
              <div className="h-4 bg-muted rounded w-24 mb-3" />
              <div className="h-8 bg-muted rounded w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const m = metrics!;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Дашборд</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Всего пользователей"
          value={formatNumber(m.totalUsers)}
          icon={Users}
          description={`+${formatNumber(m.newUsersWeek)} за неделю`}
        />
        <MetricCard
          title="MAU"
          value={formatNumber(m.mau)}
          icon={Activity}
          description="Активных за месяц"
        />
        <MetricCard
          title="DAU сегодня"
          value={formatNumber(m.dauToday)}
          icon={UserPlus}
          description="Активных за день"
        />
        <MetricCard
          title="Платящих"
          value={formatNumber(m.payingUsers)}
          icon={CreditCard}
        />
        <MetricCard
          title="Выручка за месяц"
          value={formatCurrency(m.monthlyRevenue)}
          icon={TrendingUp}
          description="За 30 дней"
        />
        <MetricCard
          title="Выручка всего"
          value={formatCurrency(m.totalRevenue)}
          icon={CreditCard}
          description="За всё время"
        />
        <MetricCard
          title="Всего AI-сотрудников"
          value={formatNumber(m.totalBots)}
          icon={Bot}
        />
        <MetricCard
          title="Конверсия"
          value={
            m.totalUsers > 0
              ? `${((m.payingUsers / m.totalUsers) * 100).toFixed(1)}%`
              : "0%"
          }
          icon={TrendingUp}
          description="В платящих"
        />
      </div>
    </div>
  );
}
